import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
    getShareCertificate,
    setShareCertificateLien
} from '../../../../redux/actions/SocietyPortal/ShareCertificateAction/ShareCertificateAction';
import { canAny } from '../../../../utils/helpers/can';
import { PERMISSIONS } from '../../../../consts/permissions';

const statusColor = (s) => {
    switch (s) {
        case 'ACTIVE': return 'tw-bg-emerald-100 tw-text-emerald-700';
        case 'LIEN_MARKED': return 'tw-bg-amber-100 tw-text-amber-700';
        case 'SURRENDERED': return 'tw-bg-gray-100 tw-text-gray-700';
        case 'CANCELLED': return 'tw-bg-red-100 tw-text-red-700';
        default: return 'tw-bg-gray-100 tw-text-gray-700';
    }
};

const ShareCertificateDetail = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userPermissions = useSelector(s => s.UserSocietyReducer?.permissions || []);
    const { detail, lien } = useSelector(state => state.ShareCertificateReducer);
    const data = detail?.data;

    const [showLienModal, setShowLienModal] = useState(false);
    const [lienHolder, setLienHolder] = useState('');

    useEffect(() => {
        if (id) dispatch(getShareCertificate({ id }));
    }, [dispatch, id]);

    const canEditLien = canAny(userPermissions, [PERMISSIONS.SHARE_CERTIFICATE_UPDATE]);

    const refresh = () => dispatch(getShareCertificate({ id }));

    const handleMarkLien = () => {
        dispatch(setShareCertificateLien({
            id,
            mark: true,
            lien_holder: lienHolder,
            onSuccess: () => {
                setShowLienModal(false);
                setLienHolder('');
                refresh();
            }
        }));
    };

    const handleReleaseLien = () => {
        dispatch(setShareCertificateLien({
            id,
            mark: false,
            onSuccess: refresh
        }));
    };

    if (detail?.loading) {
        return (
            <div className="tw-py-10 tw-flex tw-justify-center">
                <div className="tw-animate-spin tw-rounded-full tw-h-7 tw-w-7 tw-border-b-2 tw-border-blue-600" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-text-center tw-shadow-sm tw-border tw-border-gray-100">
                <p className="tw-text-sm tw-text-gray-500">Certificate not found.</p>
            </div>
        );
    }

    const isActive = data.status === 'ACTIVE';
    const isLienMarked = data.status === 'LIEN_MARKED';

    return (
        <div className="tw-space-y-4">
            <div className="tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button onClick={() => navigate(-1)} className="tw-p-1.5 tw-rounded-md tw-border tw-border-gray-200 hover:tw-bg-gray-50">
                        <Icon icon="mdi:arrow-left" className="tw-text-gray-600" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Certificate {data.certificate_number}</h1>
                        <p className="tw-text-xs tw-text-gray-500">
                            {data.flat?.wing?.title} • Flat {data.flat?.flat_number} • Issued {data.issued_date}
                        </p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <span className={`tw-px-2.5 tw-py-1 tw-rounded tw-text-xs tw-font-semibold ${statusColor(data.status)}`}>
                        {data.status}
                    </span>
                    {canEditLien && isActive && (
                        <button
                            onClick={() => setShowLienModal(true)}
                            className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-border tw-border-amber-500 tw-text-amber-700 hover:tw-bg-amber-50"
                        >
                            <Icon icon="mdi:lock-outline" className="tw-inline tw-mr-1" />
                            Mark Lien
                        </button>
                    )}
                    {canEditLien && isLienMarked && (
                        <button
                            onClick={handleReleaseLien}
                            disabled={lien?.loading}
                            className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-bg-emerald-600 tw-text-white hover:tw-bg-emerald-700 disabled:tw-opacity-60"
                        >
                            <Icon icon="mdi:lock-open-outline" className="tw-inline tw-mr-1" />
                            {lien?.loading ? 'Releasing…' : 'Release Lien'}
                        </button>
                    )}
                </div>
            </div>

            <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-3">Certificate</h3>
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3 tw-text-sm">
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Number</div>
                        <div className="tw-text-gray-800">{data.certificate_number}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Total shares</div>
                        <div className="tw-text-gray-800">{data.total_shares}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Share value</div>
                        <div className="tw-text-gray-800">₹{Number(data.share_value || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div>
                        <div className="tw-text-xs tw-text-gray-500">Total value</div>
                        <div className="tw-text-gray-800">₹{Number(data.total_value || 0).toLocaleString('en-IN')}</div>
                    </div>
                    {data.distinctive_numbers_from && (
                        <div>
                            <div className="tw-text-xs tw-text-gray-500">Distinctive from</div>
                            <div className="tw-text-gray-800">{data.distinctive_numbers_from}</div>
                        </div>
                    )}
                    {data.distinctive_numbers_to && (
                        <div>
                            <div className="tw-text-xs tw-text-gray-500">Distinctive to</div>
                            <div className="tw-text-gray-800">{data.distinctive_numbers_to}</div>
                        </div>
                    )}
                    {data.lien_holder && (
                        <div className="tw-col-span-2">
                            <div className="tw-text-xs tw-text-gray-500">Lien holder</div>
                            <div className="tw-text-gray-800">{data.lien_holder}</div>
                        </div>
                    )}
                </div>
            </div>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-2">Holder</h3>
                    <p className="tw-text-sm tw-text-gray-800">{data.entity?.name || data.entity?.first_name || '—'}</p>
                    <p className="tw-text-xs tw-text-gray-500">{data.entity?.phone}</p>
                </div>
                <div className="tw-bg-white tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
                    <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-mb-2">Flat</h3>
                    <p className="tw-text-sm tw-text-gray-800">
                        {data.flat?.wing?.title} {data.flat?.flat_number}
                    </p>
                </div>
            </div>

            {/* Lien mark modal */}
            {showLienModal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-50 tw-flex tw-items-center tw-justify-center">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-md tw-w-full tw-mx-4 tw-p-5">
                        <h3 className="tw-text-base tw-font-semibold tw-text-gray-800 tw-mb-2">Mark Lien</h3>
                        <p className="tw-text-xs tw-text-gray-500 tw-mb-3">
                            Marking lien will set the certificate status to LIEN_MARKED.
                        </p>
                        <label className="tw-text-xs tw-text-gray-600">Lien holder</label>
                        <input
                            value={lienHolder}
                            onChange={(e) => setLienHolder(e.target.value)}
                            placeholder="e.g. HDFC Bank Ltd"
                            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
                        />
                        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                            <button
                                onClick={() => setShowLienModal(false)}
                                className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-border tw-border-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleMarkLien}
                                disabled={!lienHolder.trim() || lien?.loading}
                                className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-md tw-bg-amber-600 tw-text-white hover:tw-bg-amber-700 disabled:tw-opacity-60"
                            >
                                {lien?.loading ? 'Marking…' : 'Mark Lien'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShareCertificateDetail;
